import { Button } from '../../../../components/Button/Button';
import { Cell } from '../../../../components/common/table/Cell/Cell';
import { Row } from '../../../../components/common/table/Row/Row';
import { Check } from '../../../../shared/assets/check';
import { Pencil } from '../../../../shared/assets/pencil';
import { XClose } from '../../../../shared/assets/x-close';
import clsx from 'clsx';
import { InfusionSession } from '../PatientInfusion';
import { useEffect, useState } from 'react';
import './InfusionHistoryRow.scss';
import { SingleDatePicker, toMomentObject } from 'react-dates';
import moment, { Moment } from 'moment';
import { LoadingSpinner, smallSpinner } from 'src/components/common/loader/LoadingSpinner';
import { TextInput } from 'src/components/TextInput/TextInput';
import TimeDropdown from 'src/components/common/timeSelector/timeSelector';
import { convertDate, convertTime, convertToISOTime } from 'src/components/common/convertDateTime/convertDateTime';
import { neutral300, warning } from 'src/shared/styles/variables';

export interface InfusionHistoryRowProps {
  session: InfusionSession;
  index: number;
  handleEndInfusionClick: (editedStartTime: string, editedEndTime: string, sessionId: string, sessionVersion: number) => void;
}

export const InfusionHistoryRow = ({ session, index, handleEndInfusionClick }: InfusionHistoryRowProps) => {
  const [edit, setEdit] = useState(false);
  const [tempStartTime, setTempStartTime] = useState<any>(null);
  const [tempStartDate, setTempStartDate] = useState<any>(null);
  const [tempStartDateISO, setTempStartDateISO] = useState<any>(null);

  const [tempEndTime, setTempEndTime] = useState<any>(null);
  const [sessionStatus, setSessionStatus] = useState<string>('N/A');

  const [focusedStart, setFocusedStart] = useState<boolean>(false);
  const [editing, setEditing] = useState<boolean>(false);
  const [toggleStartTimeDropDown, setToggleStartTimeDropDown] = useState<boolean>(false);
  const [toggleEndTimeDropDown, setToggleEndTimeDropDown] = useState<boolean>(false);

  const [enableActionButton, setEnableActionButton] = useState<boolean>(true);

  useEffect(() => {
    session?.body?.beg_date_time_utc ? setTempStartTime(convertTime(session?.body?.beg_date_time_utc)) : setTempStartTime(null);
    session?.body?.beg_date_time_utc ? setTempStartDate(convertDate(session?.body?.beg_date_time_utc)) : setTempStartDate(null);
    session?.body?.beg_date_time_utc ? setTempStartDateISO(session?.body?.beg_date_time_utc) : setTempStartDateISO(null);
    session?.body?.end_date_time_utc ? setTempEndTime(convertTime(session?.body?.end_date_time_utc)) : setTempEndTime(null);
    setEditing(false);
  }, [session, setTempEndTime, setTempStartTime]);

  const handleEditSubmit = () => {
    setEdit(false);
    setEditing(true);

    let endInfusionTime = tempEndTime === null || tempEndTime === '' ? '' : convertToISOTime(tempStartDate, tempEndTime);
    let startInfusionTime = convertToISOTime(tempStartDate, tempStartTime);

    if (endInfusionTime !== session?.body?.end_date_time_utc || startInfusionTime !== session?.body?.beg_date_time_utc) {
      handleEndInfusionClick(startInfusionTime || session?.body?.beg_date_time_utc, endInfusionTime || '', session.header.item_id, session.header.version);
    }
    setTempStartTime(null);
    setTempEndTime(null);
  };

  const handleEditClose = () => {
    setEdit(false);
    if (session?.body?.beg_date_time_utc) setTempStartTime(convertTime(session?.body?.beg_date_time_utc));
    if (session?.body?.beg_date_time_utc) setTempStartDate(convertDate(session?.body?.beg_date_time_utc));
    if (session?.body?.end_date_time_utc) setTempEndTime(convertTime(session?.body?.end_date_time_utc));
  };

  useEffect(() => {
    const futureTimeChecker = (startEnd: 'start' | 'end') => {
      if (!tempStartDate) return false;

      let time = startEnd === 'start' ? tempStartTime : tempEndTime;
      let inpputedDate = moment(tempStartDateISO, moment.ISO_8601);

      if (!inpputedDate) return false;

      const combinedDateTime = inpputedDate
        .clone()
        .hour(Number(time.split(':')[0]))
        .minute(Number(time.split(':')[1]))
        .second(Number(time.split(':')[2]))
        .millisecond(0);

      const formattedInput = combinedDateTime.toISOString();

      const unixTimeToCheck = new Date(formattedInput).getTime();

      const currentUnixTime = new Date().getTime();

      return unixTimeToCheck < currentUnixTime ? true : false;
    };

    if (
      tempStartTime &&
      tempEndTime &&
      tempStartTime.length === 8 &&
      tempEndTime.length === 8 &&
      tempStartTime.split(':').length === 3 &&
      tempEndTime.split(':').length === 3
    ) {
      const [hours, minutes, seconds] = tempEndTime.split(':');
      if (Number(hours) > 23 || Number(minutes) > 59 || Number(seconds) > 59) return setEnableActionButton(false);
      if (Number(hours) > 23 || Number(minutes) > 59 || Number(seconds) > 59) return setEnableActionButton(false);
      const [startHours, startMinutes, startSeconds] = tempStartTime.split(':');
      if (startHours > hours) return setEnableActionButton(false);
      if (startHours === hours && startMinutes > minutes) return setEnableActionButton(false);
      if (startHours === hours && startMinutes === minutes && startSeconds > seconds) return setEnableActionButton(false);

      if (!futureTimeChecker('start')) setEnableActionButton(false);
      if (!futureTimeChecker('end')) setEnableActionButton(false);
    }

    if (tempStartTime && (tempStartTime.length !== 8 || tempStartTime.split(':').length !== 3)) {
      return setEnableActionButton(false);
    } else if (tempStartTime) {
      if (tempStartTime.split(':').length !== 3) return setEnableActionButton(false);
      let [hours, minutes, seconds] = tempStartTime.split(':');
      if (Number(hours) > 23 || Number(minutes) > 59 || Number(seconds) > 59) return setEnableActionButton(false);
      if (!futureTimeChecker('start')) return setEnableActionButton(false);
      setEnableActionButton(true);
    } else {
      setEnableActionButton(false);
    }

    if (tempEndTime === '') {
      return setEnableActionButton(true);
    } else if (tempEndTime && (tempEndTime.length !== 8 || tempEndTime.split(':').length !== 3)) {
      return setEnableActionButton(false);
    } else if (tempEndTime && tempEndTime.length === 8 && tempEndTime.split(':').length === 3) {
      if (!futureTimeChecker('end')) return setEnableActionButton(false);
      const [hours, minutes, seconds] = tempEndTime.split(':');
      if (Number(hours) > 23 || Number(minutes) > 59 || Number(seconds) > 59) return setEnableActionButton(false);
      setEnableActionButton(true);
    }
  }, [tempStartTime, tempEndTime, tempStartDate, tempStartDateISO]);

  const toggleDropDowns = (dropDown: 'start' | 'end' | 'date') => {
    if ('start' === dropDown) {
      setToggleStartTimeDropDown(!toggleStartTimeDropDown);
      setToggleEndTimeDropDown(false);
      setFocusedStart(false);
    } else if ('end' === dropDown) {
      setToggleEndTimeDropDown(!toggleEndTimeDropDown);
      setToggleStartTimeDropDown(false);
      setFocusedStart(false);
    } else if ('date' === dropDown) {
      setToggleStartTimeDropDown(false);
      setToggleEndTimeDropDown(false);
      setFocusedStart(!focusedStart);
    }
  };

  useEffect(() => {
    if (session?.body?.status && session.body.status === 'INFUSION_STATUS_COMPLETED') {
      setSessionStatus('COMPLETED');
    } else if (session?.body?.status && session.body.status === 'INFUSION_STATUS_ACTIVE') {
      setSessionStatus('ACTIVE');
    }
  }, [session]);

  return (
    <Row index={index} key={session._id} classes={'infusion-history-row-container'}>
      <Cell>{sessionStatus}</Cell>
      <Cell>{session?.header?.user?.name}</Cell>
      <Cell>
        {edit ? (
          <SingleDatePicker
            placeholder={tempStartDate || convertDate(session?.body?.beg_date_time_utc)}
            date={toMomentObject(tempStartDateISO, 'DD-MMM-yyyy')}
            onDateChange={(inpputedDate: Moment | null) => {
              if (inpputedDate) {
                const formattedInput = inpputedDate.format('DD-MMM-yyyy');
                setTempStartDate(formattedInput);
                setTempStartDateISO(inpputedDate.toISOString());
              }
              setFocusedStart(!focusedStart);
            }}
            appendToBody={true}
            focused={focusedStart}
            onFocusChange={() => toggleDropDowns('date')}
            id="singleDatePicker"
            numberOfMonths={1}
            displayFormat="DD-MMM-yyyy"
            isOutsideRange={(day: Moment) => day.isAfter(moment().endOf('day'))}
            noBorder
            small
          />
        ) : (
          convertDate(session?.body?.beg_date_time_utc)
        )}
      </Cell>
      <Cell style={{ position: 'relative' }}>
        {edit ? (
          <>
            <TextInput
              value={tempStartTime || convertTime(session?.body?.beg_date_time_utc)}
              onChange={(e: any) => setTempStartTime(e.target.value)}
              width={95}
              borderColor={!enableActionButton ? warning : ''}
              borderWidth={!enableActionButton ? '1.5px' : ''}
              onClick={() => toggleDropDowns('start')}
            />
            <TimeDropdown
              columnWidth={60}
              isOpen={toggleStartTimeDropDown}
              onTimeSelect={({ hour, minute, second }: any) => setTempStartTime(`${hour}:${minute}:${second}`)}
              xTranslation={-30}
              startTime={tempStartTime || convertTime(session?.body?.beg_date_time_utc)}
            />
          </>
        ) : (
          tempStartTime || convertTime(session?.body?.beg_date_time_utc)
        )}
      </Cell>
      <Cell style={{ position: 'relative' }}>
        {edit ? (
          <>
            <TextInput
              value={tempEndTime || convertTime(session?.body?.end_date_time_utc)}
              onChange={(e: any) => setTempEndTime(e.target.value)}
              width={95}
              borderColor={!enableActionButton ? warning : ''}
              borderWidth={!enableActionButton ? '1.5px' : ''}
              onClick={() => toggleDropDowns('end')}
            />
            <TimeDropdown
              columnWidth={60}
              isOpen={toggleEndTimeDropDown}
              onTimeSelect={({ hour, minute, second }: any) => setTempEndTime(`${hour || '00'}:${minute || '00'}:${second || '00'}`)}
              xTranslation={-30}
              startTime={tempEndTime || convertTime(session?.body?.end_date_time_utc)}
            />
          </>
        ) : (
          tempEndTime || convertTime(session?.body?.end_date_time_utc)
        )}
      </Cell>
      <Cell style={{ paddingLeft: '1em' }}>
        {
          <div className="history-actions-container">
            {edit && (
              <>
                <Button
                  icon={<XClose fill="#2CC0E8" />}
                  onClick={() => handleEditClose()}
                  width={'40px'}
                  height={'40px'}
                  className={clsx('square-icon-button', 'cancel-button')}
                />
                <Button
                  icon={<Check fill="#FBFBFB" />}
                  onClick={() => handleEditSubmit()}
                  width={'40px'}
                  height={'40px'}
                  background-color={!enableActionButton ? neutral300 : null}
                  disabled={!enableActionButton}
                  className={clsx('square-icon-button', 'save-button')}
                />
              </>
            )}
            {!edit && (
              <Button
                icon={editing ? <LoadingSpinner width={smallSpinner} height={smallSpinner} /> : <Pencil fill="#2CC0E8" />}
                onClick={() => setEdit(true)}
                width={'40px'}
                height={'40px'}
                className={clsx('square-icon-button', 'edit-button-enabled')}
              ></Button>
            )}
          </div>
        }
      </Cell>
    </Row>
  );
};
